import { BLACKOUT_SCOPES, DATE_VALUE_FORMAT, formatEntries, formatEntry, formatTimeRange, includesId, isAllDayEntry, isSameId } from "helpers/blackouts"
import { FILTER_SUMMARY_MAX_NAMES } from "./constants"

// Entry date/time formatting is shared with the Calendar surfaces — see `helpers/blackouts`.
export { isAllDayEntry, isRangeEntry, formatTimeRange, formatEntry, formatEntries } from "helpers/blackouts"

/**
 * The date line for a row: entries, plus the time range when a single entry is timed.
 * A multi-entry blackout keeps just the dates — per-entry times would not fit the row.
 */
export const formatRowDateLabel = (blackout) => {
  const entries = blackout?.entries || []
  const dates = formatEntries(entries)
  if (entries.length !== 1 || isAllDayEntry(entries[0])) return dates
  return `${dates} · ${formatTimeRange(entries[0])}`
}

/**
 * Row identity for an unsaved "Applies on" entry.
 *
 * Saved entries keep the server's `entries[].id`. This only fills in rows the guide has just
 * added, and rows the API returns without an id — `AppliesOnRows` keys, edits and deletes by
 * id, so two rows sharing one would be edited and removed together.
 */
export const createEntryId = () => `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

/**
 * A saved record's entries, each with a usable row id.
 *
 * The server's `entries[].id` is preferred — but only when a record's ids are actually distinct.
 * The API has shipped `"e-1"` on every entry, and now returns the record's own id on the entry,
 * so uniqueness is not something to assume. `AppliesOnRows` keys, edits and deletes rows by this
 * id, so a single collision would edit or remove several rows at once. Any duplicate or missing
 * id falls the whole record back to client ids, which are ignored on write (D6) and cost nothing.
 */
export const withRowIds = (entries = []) => {
  const ids = entries.map((entry) => entry.id)
  const areIdsUsable = ids.every(Boolean) && new Set(ids.map(String)).size === ids.length
  return entries.map((entry) => ({ ...entry, id: areIdsUsable ? entry.id : createEntryId() }))
}

/** A fresh, empty "Applies on" row. */
export const createEmptyEntry = () => ({
  id: createEntryId(),
  start_date: null,
  end_date: null,
  start_time: null,
  end_time: null
})

export const ENTRY_ERRORS = {
  MISSING_DATE: "missing_date",
  INCOMPLETE_TIME: "incomplete_time",
  TIME_ORDER: "time_order",
  DUPLICATE: "duplicate",
  OVERLAP: "overlap"
}

/**
 * What is wrong with one "Applies on" row, or null.
 *
 * A dateless row is only an error once the guide has added a second one: the modal opens with a
 * single empty row, and flagging that would put an error on an untouched form. With more than one
 * row it matters — `buildBlackoutPayload` drops undated rows, so without this the guide fills two
 * of three rows, saves, and the third is gone with nothing said.
 *
 * Times are compared as "HH:mm" strings, which orders correctly. An end at or before the start is
 * rejected rather than read as crossing midnight — an entry's times apply *within* each of its
 * days, so there is no shape here that can express an overnight window.
 */
export const getEntryError = (entry, { isOnlyEntry = false } = {}) => {
  if (!entry?.start_date) return isOnlyEntry ? null : ENTRY_ERRORS.MISSING_DATE

  const hasStartTime = Boolean(entry.start_time)
  const hasEndTime = Boolean(entry.end_time)
  if (hasStartTime !== hasEndTime) return ENTRY_ERRORS.INCOMPLETE_TIME
  if (hasStartTime && entry.end_time <= entry.start_time) return ENTRY_ERRORS.TIME_ORDER

  return null
}

/**
 * A row's dates as a comparable range. `YYYY-MM-DD` strings order correctly as strings, and a
 * single-day row is a range whose ends meet.
 */
const toDateRange = (entry) => ({ start: entry.start_date, end: entry.end_date || entry.start_date })

const isSameRange = (first, second) => first.start === second.start && first.end === second.end

const isOverlappingRange = (first, second) => first.start <= second.end && second.start <= first.end

/**
 * Row errors keyed by entry id; an empty object means the whole list is valid.
 *
 * Two rows may not cover the same day. Times are not part of the comparison: a day is either
 * blacked out or it is not, so a second row reaching into days the first already closes says
 * nothing new — whatever window is wanted belongs on the one row. An exact repeat and a partial
 * overlap are told apart only so the message can name what is wrong; both block the form.
 *
 * Only the later row is marked, so the error names the row to fix, and only rows that are
 * themselves valid become a baseline for the ones below. An undated row is never compared — it is
 * either the untouched first row or already carrying `MISSING_DATE`.
 */
export const getEntryErrors = (entries = []) => {
  const acceptedRanges = []

  return entries.reduce((errors, entry) => {
    const error = getEntryError(entry, { isOnlyEntry: entries.length === 1 })
    if (error) {
      errors[entry.id] = error
      return errors
    }
    if (!entry.start_date) return errors

    const range = toDateRange(entry)
    const clash = acceptedRanges.find((accepted) => isOverlappingRange(accepted, range))
    if (clash) errors[entry.id] = isSameRange(clash, range) ? ENTRY_ERRORS.DUPLICATE : ENTRY_ERRORS.OVERLAP
    else acceptedRanges.push(range)
    return errors
  }, {})
}

/**
 * Flattens the nested target options into the leaf ids the API stores.
 * Selecting a parent (e.g. "Kayak · 5 assets") means selecting all of its assets.
 */
export const collectSelectableIds = (options = []) =>
  options.reduce((ids, option) => {
    if (option.children?.length) return [...ids, ...option.children.map((child) => child.id)]
    return [...ids, option.id]
  }, [])

/** Looks up an option or child option by id so chips can render its label. */
export const findOptionById = (options = [], id) => {
  for (const option of options) {
    if (isSameId(option.id, id)) return option
    const child = option.children?.find((candidate) => isSameId(candidate.id, id))
    if (child) return child
  }
  return null
}

/** "Leslie Alexander (Capitan)" — label plus optional suffix. */
export const formatOptionLabel = (option) => (option?.suffix ? `${option.label} ${option.suffix}` : option?.label || "")

/** True when every child of a parent option is selected. */
export const areAllChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) && option.children.every((child) => includesId(selectedIds, child.id))

/** True when some but not all children are selected — the parent renders indeterminate. */
export const areSomeChildrenSelected = (option, selectedIds = []) =>
  Boolean(option.children?.length) &&
  option.children.some((child) => includesId(selectedIds, child.id)) &&
  !areAllChildrenSelected(option, selectedIds)

/**
 * Chips collapse to the parent when all of its children are selected, and expand to
 * individual assets otherwise — the two states visible in the equipment design.
 */
export const buildSelectedChips = (options = [], selectedIds = []) =>
  options.reduce((chips, option) => {
    if (!option.children?.length) {
      return includesId(selectedIds, option.id) ? [...chips, { id: option.id, label: formatOptionLabel(option) }] : chips
    }
    if (areAllChildrenSelected(option, selectedIds)) {
      return [...chips, { id: option.id, label: formatOptionLabel(option), childIds: option.children.map((child) => child.id) }]
    }
    const selectedChildren = option.children.filter((child) => includesId(selectedIds, child.id))
    return [...chips, ...selectedChildren.map((child) => ({ id: child.id, label: formatOptionLabel(child) }))]
  }, [])

/**
 * Strips UI-only fields before sending a blackout to the API.
 *
 * A business blackout applies to everything and has no picker, so `target_ids` is forced empty
 * rather than passed through — the API has been seen returning a non-empty `target_ids` on a
 * business record, and editing must not echo that back as if the guide had chosen it.
 */
export const buildBlackoutPayload = ({ scope, reason, isRecurringYearly, entries, selectedIds }) => ({
  scope,
  reason: reason?.trim() || "",
  recurring_yearly: Boolean(isRecurringYearly),
  entries: entries
    .filter((entry) => entry.start_date)
    .map((entry) => ({
      start_date: entry.start_date,
      end_date: entry.end_date || entry.start_date,
      start_time: entry.start_time || null,
      end_time: entry.end_time || null
    })),
  target_ids: scope === BLACKOUT_SCOPES.BUSINESS ? [] : selectedIds || []
})

/**
 * The Date Range pill's label, e.g. "Jun 4 - Jul 2, 2026".
 *
 * Reuses `formatEntry` so the pill collapses a shared month or year exactly as a blackout's own
 * date line does; the dayjs pair is normalised to date strings first so a single-day range does
 * not read as "Jun 4 - 4, 2026".
 */
export const formatFilterRangeLabel = (range) => {
  if (!range?.[0] || !range?.[1]) return ""
  return formatEntry({ start_date: range[0].format(DATE_VALUE_FORMAT), end_date: range[1].format(DATE_VALUE_FORMAT) }, true)
}

/**
 * Target options as the flat leaves a filter checkbox list needs.
 *
 * `collectSelectableIds` does the same walk for ids alone, which is all the form's picker needs;
 * the filters have to render the text too. `suffix` stays separate from `label` rather than being
 * joined by `formatOptionLabel`, because the design greys it — "Dave Brown (Captain)".
 */
export const flattenTargetOptions = (options = []) => {
  const toLeaf = (option) => ({ id: option.id, label: option.label || "", suffix: option.suffix || "" })
  return options.flatMap((option) => (option.children?.length ? option.children.map(toLeaf) : [toLeaf(option)]))
}

/**
 * Filter state -> the query the list endpoint is asked for.
 *
 * Empty filters are omitted rather than sent as empty arrays, so an unfiltered page issues the
 * same bare `GET /guide/blackouts` it always has. See `docs/blackouts-api.md` §2.1.
 */
export const buildBlackoutListParams = ({ dateRange, resourceIds, listingIds } = {}) => {
  const params = {}
  if (dateRange?.[0] && dateRange?.[1]) {
    params.start_date = dateRange[0].format(DATE_VALUE_FORMAT)
    params.end_date = dateRange[1].format(DATE_VALUE_FORMAT)
  }
  if (resourceIds?.length) params.resource_ids = resourceIds
  if (listingIds?.length) params.listing_ids = listingIds
  return params
}


/**
 * What a collapsed filter section says it is filtering by.
 *
 * A short selection is spelled out, because the names are what the guide recognises; past
 * `FILTER_SUMMARY_MAX_NAMES` the string would be longer than the row, so it falls back to a count
 * per group ("5 crew · 4 equipment") — which is also why groups are passed rather than options.
 */
export const formatTargetsSummary = (selectedIds = [], groups = [], { countLabel } = {}) => {
  if (!selectedIds.length) return ""

  const selectedByGroup = groups.map((group) => ({
    label: group.label,
    options: group.options.filter((option) => includesId(selectedIds, option.id))
  }))
  const selected = selectedByGroup.flatMap((group) => group.options)

  // An id with no matching option (a stale filter, or options still loading) still counts.
  if (!selected.length) return countLabel ? countLabel(selectedIds.length) : String(selectedIds.length)

  if (selected.length <= FILTER_SUMMARY_MAX_NAMES) {
    return selected.map((option) => `${option.label}${option.suffix ? ` ${option.suffix}` : ""}`).join(", ")
  }

  const withCounts = selectedByGroup.filter((group) => group.options.length > 0)
  if (withCounts.length > 1 || withCounts[0]?.label) {
    return withCounts.map((group) => `${group.options.length} ${group.label.toLowerCase()}`).join(" · ")
  }
  return countLabel ? countLabel(selected.length) : String(selected.length)
}
